import { IconCircleX, IconMoodSmile } from "@tabler/icons-react";
import classNames from "classnames";
import React, {
  InputHTMLAttributes,
  useEffect,
  useImperativeHandle,
} from "react";
import { __ } from "@common/helpers/i18n";
import { TextInputProps } from "@common/types";
import { InputPrimitive } from "@app/components/ui/input";
import { EmojiPicker, EmojiPickerProps } from "@app/elements/form/EmojiPicker";
import { useDocumentDirection } from "@app/hooks";
import { CounterBadge } from "../text";
import { Input } from "./Input";
import { getResolvedFieldPlaceholder } from "./fieldPlaceholder";

function getValueLength(value: unknown) {
  if (typeof value === "string" || typeof value === "number") {
    return String(value).length;
  }

  return 0;
}

export const TextInput = React.forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & TextInputProps
>(function TextInput(
  {
    label,
    error,
    hint,
    loading,
    className,
    icon,
    iconPosition,
    emojiPicker,
    emojiIcon,
    success,
    onChange,
    onInputChange,
    onClear,
    autoWidth,
    maxLength,
    footer,
    placeholder,
    ...props
  },
  ref,
) {
  const [length, setLength] = React.useState(() =>
    getValueLength(props.value ?? props.defaultValue),
  );

  const [emojiPickerVisible, setEmojiPickerVisible] = React.useState(false);
  const innerRef = React.useRef<HTMLInputElement>(null);
  const controlDir = useDocumentDirection();
  const resolvedDir = props.dir || controlDir;
  const currentLength =
    props.value !== undefined ? getValueLength(props.value) : length;
  const remaining =
    typeof maxLength === "number" ? maxLength - currentLength : 0;
  const hasCounter = typeof maxLength === "number";
  const hasVisibleIcon = Boolean(icon && icon !== true);
  const resolvedIconPosition = iconPosition ?? "start";
  const hasStartIcon = hasVisibleIcon && resolvedIconPosition !== "end";
  const hasEndIcon =
    (hasVisibleIcon && resolvedIconPosition === "end") ||
    emojiPicker ||
    Boolean(onClear);
  const resolvedPlaceholder = getResolvedFieldPlaceholder(
    label,
    placeholder,
    "input",
    props.type,
  );
  const normalizeValue = React.useCallback(
    (value: string) =>
      typeof maxLength === "number" ? value.slice(0, maxLength) : value,
    [maxLength],
  );

  useImperativeHandle(ref, () => innerRef.current!);

  useEffect(() => {
    function closeEmoji() {
      setEmojiPickerVisible(false);
    }
    document.addEventListener("click", closeEmoji);
    return () => document.removeEventListener("click", closeEmoji);
  }, []);

  const onEmojiSelect: EmojiPickerProps["onEmojiInsert"] = (emoji) => {
    setEmojiPickerVisible(false);
    if (!innerRef.current) {
      return;
    }

    const el = innerRef.current;
    const cursorPosition = el.selectionStart;
    if (cursorPosition || cursorPosition === 0) {
      const textBeforeCursorPosition = el.value.substring(0, cursorPosition);
      const textAfterCursorPosition = el.value.substring(
        cursorPosition,
        el.value.length,
      );
      const nextValue = normalizeValue(
        textBeforeCursorPosition + emoji.native + textAfterCursorPosition,
      );
      onInputChange?.(nextValue);
      setLength(nextValue.length);
    }
  };

  return (
    <Input
      label={label}
      error={error}
      hint={hint}
      loading={loading}
      icon={icon || emojiPicker}
      iconPosition={iconPosition}
      success={success}
      className={className}
      footer={
        hasCounter || footer ? (
          <>
            {hasCounter && (
              <CounterBadge
                value={__("%{count} characters remaining", {
                  count: remaining,
                })}
                tone={remaining < 0 ? "danger" : "neutral"}
                size="sm"
                className={classNames(
                  "mt-1.5 flex w-fit",
                  currentLength === 0 && "invisible",
                )}
              />
            )}
            {footer}
          </>
        ) : undefined
      }
    >
      <InputPrimitive
        variant={
          error
            ? "danger"
            : success
              ? "success"
              : props.disabled
                ? "disabled"
                : "default"
        }
        className={classNames(
          autoWidth ? "w-auto" : "block w-full",
          className,
          resolvedDir === "rtl" ? "text-right" : "text-left",
          "input-padding",
          hasStartIcon && "ps-11",
          hasEndIcon && "pe-11",
        )}
        data-input-icon-start={hasStartIcon ? "true" : undefined}
        data-input-icon-end={hasEndIcon ? "true" : undefined}
        data-input-emoji={emojiPicker ? "true" : undefined}
        ref={innerRef}
        type="text"
        maxLength={maxLength}
        placeholder={resolvedPlaceholder}
        onChange={(e) => {
          const nextValue = normalizeValue(e.target.value);
          e.target.value = nextValue;
          onChange?.(e);
          onInputChange?.(nextValue);
          setLength(nextValue.length);
        }}
        {...props}
        dir={resolvedDir}
      />
      {onClear && (
        <IconCircleX
          onClick={() => onClear()}
          size={18}
          stroke={2}
          className="absolute end-4 top-1/2 -translate-y-1/2 cursor-pointer text-muted-foreground transition-colors hover:text-foreground"
        />
      )}

      {emojiPicker && (
        <EmojiPicker
          isOpen={emojiPickerVisible}
          onEmojiInsert={onEmojiSelect}
          renderTarget={(ref: any) => (
            <div
              ref={ref}
              className="absolute end-2 top-5 hidden -translate-y-1/2 cursor-pointer items-center md:flex me-1"
              onClick={(e) => {
                setEmojiPickerVisible(!emojiPickerVisible);
                e.stopPropagation();
              }}
            >
              {emojiIcon || (
                <IconMoodSmile
                  size={18}
                  stroke={2}
                  className="text-muted-foreground"
                />
              )}
            </div>
          )}
        />
      )}
    </Input>
  );
});
